import { DestroyRef, inject, Injectable } from '@angular/core';
import { blobToBase64, getCamera, getMediaRecorder, waitForElement } from './video-utils';
import { BehaviorSubject } from 'rxjs';
import { VideoRecordingManagerService } from '@jump-tech-frontend/domain';

const DATA_FLUSH_INTERVAL_MS = 1000;
const STOP_SAFETY_TIMEOUT_MS = 3000;
const MIN_RECORDING_SIZE_BYTES = 10 * 1024;
const DEFAULT_CONTAINER_TYPE = 'video/mp4';

interface PendingStop {
  saved: Promise<void>;
  resolve: () => void;
}

@Injectable()
export class VideoRecorderService {
  private destroyRef = inject(DestroyRef);
  private videoRecordingManager = inject(VideoRecordingManagerService, { optional: true });

  private recordedChunks: Blob[] = [];
  private stream: MediaStream | undefined;
  private video: HTMLVideoElement;
  private videoId: string;
  private mediaRecorder?: MediaRecorder;
  private onRecordingFailed?: () => void;

  private flaggedForPause = false;
  private flaggedForStop = false;
  private pendingStop: PendingStop | null = null;
  private stopSafetyTimer: ReturnType<typeof setTimeout> | null = null;

  private recordingSubject = new BehaviorSubject<string | null>(null);
  private recording$ = this.recordingSubject.asObservable();

  public init(videoId: string, video?: string, onRecordingFailed?: () => void) {
    this.videoId = videoId;
    this.onRecordingFailed = onRecordingFailed;
    this.loadExistingVideo(video);
    this.updateRecording(video);
    this.registerWithRecordingManager();
    this.registerCleanup();
    return this.recording$;
  }

  public async startRecording() {
    this.resetRecordingState();

    const videoElement = (await waitForElement(this.videoId)) as HTMLVideoElement;
    if (!videoElement) {
      return;
    }

    this.video = videoElement;
    this.prepareVideoElementForRecording();

    this.stream = await getCamera('environment');
    if (!this.stream || this.flaggedForStop) {
      this.flaggedForStop = false;
      return;
    }

    this.video.srcObject = this.stream;
    this.mediaRecorder = getMediaRecorder(this.stream, this.handleDataAvailable, this.handleRecorderStopped);
    if (!this.mediaRecorder) {
      return;
    }

    this.mediaRecorder.start(DATA_FLUSH_INTERVAL_MS);
    if (this.flaggedForPause) {
      this.mediaRecorder.pause();
    }
  }

  /**
   * Resolves once the recording has been saved, or rejected as too small.
   */
  public stopRecording(): Promise<void> {
    this.flaggedForStop = true;

    if (this.pendingStop) {
      return this.pendingStop.saved;
    }
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      return Promise.resolve();
    }
    return this.stopAndSave(this.mediaRecorder);
  }

  public pauseRecording() {
    this.flaggedForPause = true;
    this.mediaRecorder?.pause();
  }

  public resumeRecording() {
    this.flaggedForPause = false;
    this.mediaRecorder?.resume();
  }

  public async removeRecording() {
    const videoElement = (await waitForElement(this.videoId)) as HTMLVideoElement;
    if (!videoElement) {
      return;
    }
    videoElement.srcObject = null;
    videoElement.load();
  }

  public updateRecording(video?: string) {
    this.recordingSubject.next(video ?? null);
  }

  /**
   * Stays true while a stopped recording is still being saved.
   */
  public isRecording(): boolean {
    return (
      this.pendingStop !== null || this.mediaRecorder?.state === 'recording' || this.mediaRecorder?.state === 'paused'
    );
  }

  private handleDataAvailable = (e: BlobEvent) => {
    if (e.data.size > 0) {
      this.recordedChunks.push(e.data);
    }

    if (this.flaggedForPause && this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder?.pause();
      this.flaggedForPause = false;
    }
  };

  private handleRecorderStopped = () => {
    this.finaliseRecording();
  };

  /**
   * The browser sends the last of the media before its stop event, so the recording is saved there.
   * The safety timeout covers a browser that never sends the stop event.
   */
  private stopAndSave(mediaRecorder: MediaRecorder): Promise<void> {
    let resolve!: () => void;
    const saved = new Promise<void>(r => (resolve = r));
    this.pendingStop = { saved, resolve };
    this.stopSafetyTimer = setTimeout(() => this.finaliseRecording(), STOP_SAFETY_TIMEOUT_MS);
    mediaRecorder.stop();
    return saved;
  }

  private async finaliseRecording() {
    const pendingStop = this.pendingStop;
    if (!pendingStop) {
      return;
    }
    this.clearStopSafetyTimer();

    try {
      const blob = new Blob(this.recordedChunks, { type: this.getContainerType() });
      this.releaseMediaResources();

      if (blob.size < MIN_RECORDING_SIZE_BYTES) {
        this.reportFailedRecording(blob);
        return;
      }

      this.displayRecordedVideo(blob);
      await this.saveRecordingAsBase64(blob);
    } finally {
      this.pendingStop = null;
      pendingStop.resolve();
    }
  }

  private clearStopSafetyTimer() {
    if (this.stopSafetyTimer) {
      clearTimeout(this.stopSafetyTimer);
      this.stopSafetyTimer = null;
    }
  }

  /**
   * Codec parameters are dropped because the upload only accepts a plain `type/subtype` data URL.
   */
  private getContainerType(): string {
    return this.mediaRecorder?.mimeType?.split(';')[0] || DEFAULT_CONTAINER_TYPE;
  }

  private reportFailedRecording(blob: Blob) {
    console.error('Video recording is too small to contain any frames', {
      sizeBytes: blob.size,
      chunks: this.recordedChunks.length,
      mimeType: this.mediaRecorder?.mimeType,
      userAgent: navigator.userAgent
    });
    this.onRecordingFailed?.();
  }

  private releaseMediaResources() {
    if (this.video) {
      this.video.srcObject = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
      });
      this.stream = undefined;
    }
  }

  private displayRecordedVideo(blob: Blob) {
    if (this.video) {
      this.video.src = URL.createObjectURL(blob);
      this.video.autoplay = false;
      this.video.controls = true;
      this.video.muted = false;
      this.video.load();
    }
  }

  private async saveRecordingAsBase64(blob: Blob) {
    const base64 = await blobToBase64(blob, blob.type);
    this.updateRecording(base64);
  }

  private resetRecordingState() {
    this.clearStopSafetyTimer();
    this.pendingStop = null;
    this.recordedChunks = [];
    this.flaggedForPause = false;
    this.flaggedForStop = false;
  }

  private prepareVideoElementForRecording() {
    this.video.autoplay = true;
    this.video.controls = false;
    this.video.muted = true;
  }

  private loadExistingVideo(video?: string) {
    if (video?.length) {
      waitForElement(this.videoId).then(videoElement => {
        this.video = videoElement as HTMLVideoElement;
        if (this.video && !this.video.src) {
          this.video.src = video ?? '';
          this.video.load();
        }
      });
    }
  }

  private registerWithRecordingManager() {
    if (this.videoRecordingManager) {
      this.videoRecordingManager.registerRecording({
        id: this.videoId,
        stopRecording: () => this.stopRecording(),
        isRecording: () => this.isRecording()
      });
    }
  }

  private registerCleanup(): void {
    try {
      this.destroyRef.onDestroy(() => this.performCleanup());
    } catch {
      this.performCleanup();
    }
  }

  private performCleanup(): void {
    try {
      this.stopRecording();
      if (this.videoRecordingManager) {
        this.videoRecordingManager.unregisterRecording(this.videoId);
      }
    } catch {
      // Cleanup errors are non-critical
    }
  }
}
